'use client';

import { useEffect } from 'react';
import { setLocale } from '@/lib/i18n';
import { getLangPref, setLangPref, clearLangPref } from '@/lib/user-preferences';

interface LangAlternate {
  lang: string;
  slug: string;
  label: string;
}

/**
 * Auto-detect browser language and redirect to the matching language variant
 * if available. Respects manual overrides stored in a cookie.
 */
export function useAutoLang(
  langAlternates: LangAlternate[],
  currentLang: string,
) {
  useEffect(() => {
    if (langAlternates.length < 2) return;

    const override = getLangPref();

    if (override) {
      // User previously chose a language — redirect if current page doesn't match
      if (override !== currentLang) {
        const match = langAlternates.find(alt => alt.lang === override);
        if (match) {
          window.location.replace('/' + match.slug);
        }
      }
      return;
    }

    // No override — auto-detect from browser language
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    if (!browserLang || browserLang === currentLang) return;

    const match = langAlternates.find(alt => alt.lang === browserLang);
    if (match && match.lang !== currentLang) {
      window.location.replace('/' + match.slug);
    }
  }, [langAlternates, currentLang]);
}

/**
 * Set the manual language override. Call this when user clicks a language link.
 */
export function setLangOverride(lang: string) {
  setLangPref(lang);
  // Keep module-level currentLocale in sync so t() returns correct strings
  if (lang === 'en' || lang === 'id') {
    setLocale(lang);
  }
}

/**
 * Clear the manual language override, reverting to browser auto-detection.
 */
export function clearLangOverride() {
  clearLangPref();
}
